/* An abstract sea: warm paper slowly invaded by moving crimson ink. */
(function () {
  'use strict';

  var VERTEX_SOURCE = [
    'attribute vec2 a_position;',
    'varying vec2 v_uv;',
    'void main() {',
    '  v_uv = a_position * 0.5 + 0.5;',
    '  gl_Position = vec4(a_position, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FRAGMENT_SOURCE = [
    'precision __PRECISION__ float;',
    'varying vec2 v_uv;',
    'uniform vec2 u_resolution;',
    'uniform float u_time;',
    'uniform float u_blood;',
    '',
    'float hash(vec2 p) {',
    '  p = fract(p * vec2(123.34, 345.45));',
    '  p += dot(p, p + 34.345);',
    '  return fract(p.x * p.y);',
    '}',
    '',
    'float noise(vec2 p) {',
    '  vec2 cell = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 s = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash(cell), hash(cell + vec2(1.0, 0.0)), s.x),',
    '             mix(hash(cell + vec2(0.0, 1.0)), hash(cell + 1.0), s.x), s.y);',
    '}',
    '',
    'float fbm(vec2 p) {',
    '  float value = 0.0;',
    '  float weight = 0.5;',
    '  mat2 turn = mat2(0.80, -0.60, 0.60, 0.80);',
    '  for (int octave = 0; octave < 4; octave++) {',
    '    value += weight * noise(p);',
    '    p = turn * p * 2.03 + vec2(9.2, 3.7);',
    '    weight *= 0.5;',
    '  }',
    '  return value;',
    '}',
    '',
    'void main() {',
    '  vec2 uv = v_uv;',
    '  float aspect = u_resolution.x / max(u_resolution.y, 1.0);',
    '  vec2 p = (uv - 0.5) * vec2(aspect, 1.0);',
    '  float time = u_time * 0.075;',
    '  float blood = clamp(u_blood, 0.0, 1.0);',
    '',
    '  // Slow domain warping makes the advancing edge curl and split.',
    '  vec2 tide = vec2(',
    '    sin(p.y * 3.8 + time * 0.62) + cos(p.x * 2.1 - time * 0.41),',
    '    cos(p.x * 3.4 + time * 0.37) - sin(p.y * 2.7 - time * 0.53)',
    '  ) * 0.16;',
    '  float broad = fbm(p * 3.5 + tide + vec2(time * 0.19, -time * 0.12));',
    '  vec2 current = p * 8.0 + tide * 2.4 + vec2(broad * 2.5, broad * -1.8);',
    '  float veins = fbm(current + vec2(-time * 0.22, time * 0.14));',
    '  float detail = noise(current * 4.1 + vec2(time * 0.32, -time * 0.21));',
    '',
    '  // Distance from all four shores, with different inlets along each.',
    '  float shore = min(min(uv.x, 1.0 - uv.x) * aspect, min(uv.y, 1.0 - uv.y));',
    '  shore /= max(min(aspect, 1.0) * 0.5, 0.01);',
    '  float arrival = shore * 0.74 + broad * 0.45 + veins * 0.22;',
    '  arrival += sin(veins * 16.0 + broad * 5.0) * 0.035;',
    '  float front = blood * 1.50 - 0.17;',
    '  float wet = smoothstep(arrival - 0.045, arrival + 0.035, front);',
    '  // Explicit endpoints guarantee a clear first frame and a complete sea.',
    '  wet *= smoothstep(0.015, 0.09, blood);',
    '  wet = mix(wet, 1.0, smoothstep(0.94, 1.0, blood));',
    '  float leading = (1.0 - smoothstep(0.0, 0.14, abs(front - arrival))) * wet;',
    '',
    '  float fibre = (hash(gl_FragCoord.xy) - 0.5) * 0.007;',
    '  vec3 paper = vec3(0.953, 0.941, 0.910) + fibre;',
    '  paper -= (broad - 0.45) * 0.015;',
    '',
    '  // Opaque wine-red depth with brighter translucent ink at the tide line.',
    '  vec3 deep = vec3(0.120, 0.020, 0.055);',
    '  vec3 crimson = vec3(0.370, 0.043, 0.133);',
    '  float depth = clamp(broad * 0.70 + veins * 0.58, 0.0, 1.0);',
    '  vec3 sea = mix(deep, crimson, depth);',
    '  sea = mix(sea, vec3(0.430, 0.048, 0.137), leading * 0.30);',
    '',
    '  // Fine grazing reflections follow the same current as the ink.',
    '  float wave = sin(p.y * 145.0 + p.x * 17.0 + veins * 19.0 + time * 1.6);',
    '  float glint = pow(max(wave, 0.0), 14.0);',
    '  float lightPool = exp(-dot((p - vec2(-0.20, 0.12)) * vec2(1.5, 1.1),',
    '                             (p - vec2(-0.20, 0.12)) * vec2(1.5, 1.1)) * 2.2);',
    '  sea += vec3(0.10, 0.045, 0.060) * glint * lightPool * 0.12;',
    '  sea += (detail - 0.5) * 0.009;',
    '  float vignette = smoothstep(0.17, 0.80, length(uv - 0.5));',
    '  sea *= 1.0 - vignette * 0.27;',
    '  vec3 colour = mix(paper, sea, wet);',
    '  gl_FragColor = vec4(colour, 1.0);',
    '}'
  ].join('\n');

  function YamSuphBloodSea(canvas) {
    this.canvas = canvas;
    this.gl = null;
    this.program = null;
    this.buffer = null;
    this.uniforms = null;
    this.available = false;
    this.destroyed = false;
    this.lastFrame = { time: 0, blood: 0 };
    this.onContextLost = this.handleContextLost.bind(this);
    this.onContextRestored = this.handleContextRestored.bind(this);

    if (!canvas || typeof canvas.getContext !== 'function') return;
    canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);
    this.initialise();
  }

  YamSuphBloodSea.prototype.initialise = function () {
    var gl;
    try {
      gl = this.canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false
      });
    } catch (error) {
      return false;
    }
    if (!gl) return false;
    this.gl = gl;

    var vertex = null;
    var fragment = null;
    var program = null;
    var buffer = null;
    try {
      var highp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
      var precision = highp && highp.precision > 0 ? 'highp' : 'mediump';
      vertex = this.compileShader(gl.VERTEX_SHADER, VERTEX_SOURCE);
      fragment = this.compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SOURCE.replace('__PRECISION__', precision));
      if (!vertex || !fragment) throw new Error('Unable to compile the sea shader.');
      program = gl.createProgram();
      if (!program) throw new Error('Unable to allocate the sea program.');
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || 'Unable to link the sea program.');
      }

      buffer = gl.createBuffer();
      if (!buffer) throw new Error('Unable to allocate the sea geometry.');
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
      var position = gl.getAttribLocation(program, 'a_position');
      if (position < 0) throw new Error('Sea geometry attribute is unavailable.');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);
      this.program = program;
      this.buffer = buffer;
      this.uniforms = {
        resolution: gl.getUniformLocation(program, 'u_resolution'),
        time: gl.getUniformLocation(program, 'u_time'),
        blood: gl.getUniformLocation(program, 'u_blood')
      };
      this.available = true;
      return true;
    } catch (error) {
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      this.available = false;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[YamSuph] Sea effect unavailable; using the page background.', error.message);
      }
      return false;
    } finally {
      if (vertex) gl.deleteShader(vertex);
      if (fragment) gl.deleteShader(fragment);
    }
  };

  YamSuphBloodSea.prototype.compileShader = function (type, source) {
    var gl = this.gl;
    var shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      var message = gl.getShaderInfoLog(shader) || 'Unknown shader compilation error.';
      gl.deleteShader(shader);
      throw new Error(message);
    }
    return shader;
  };

  YamSuphBloodSea.prototype.resize = function (width, height, dpr) {
    if (this.destroyed || !this.canvas) return false;
    var ratio = Math.min(1, Math.max(0.1, Number(dpr) || 1));
    var nextWidth = Math.max(1, Math.round((Number(width) || 1) * ratio));
    var nextHeight = Math.max(1, Math.round((Number(height) || 1) * ratio));
    if (this.gl && !this.gl.isContextLost()) {
      var maxSize = this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE) || 4096;
      var viewportLimits = this.gl.getParameter(this.gl.MAX_VIEWPORT_DIMS);
      var maxWidth = Math.min(maxSize, viewportLimits ? viewportLimits[0] : maxSize);
      var maxHeight = Math.min(maxSize, viewportLimits ? viewportLimits[1] : maxSize);
      var scale = Math.min(1, maxWidth / nextWidth, maxHeight / nextHeight);
      nextWidth = Math.max(1, Math.floor(nextWidth * scale));
      nextHeight = Math.max(1, Math.floor(nextHeight * scale));
    }
    if (this.canvas.width !== nextWidth) this.canvas.width = nextWidth;
    if (this.canvas.height !== nextHeight) this.canvas.height = nextHeight;

    if (this.gl && !this.gl.isContextLost()) {
      // Browsers can allocate a smaller backing surface than canvas requests,
      // even below their advertised limits. Fit both axes together so a tall
      // mobile canvas retains its aspect ratio instead of clipping its bottom.
      var allocatedWidth = this.gl.drawingBufferWidth;
      var allocatedHeight = this.gl.drawingBufferHeight;
      if (allocatedWidth > 0 && allocatedHeight > 0) {
        var allocatedScale = Math.min(1, allocatedWidth / nextWidth, allocatedHeight / nextHeight);
        if (allocatedScale < 1) {
          nextWidth = Math.max(1, Math.floor(nextWidth * allocatedScale));
          nextHeight = Math.max(1, Math.floor(nextHeight * allocatedScale));
          if (this.canvas.width !== nextWidth) this.canvas.width = nextWidth;
          if (this.canvas.height !== nextHeight) this.canvas.height = nextHeight;
        }
      }
    }

    // Changing canvas dimensions clears its pixels, including while the parent
    // animation is paused. Redraw immediately without starting an animation loop.
    if (this.available) return this.render(this.lastFrame.time, this.lastFrame.blood);
    return this.available;
  };

  YamSuphBloodSea.prototype.render = function (timeSeconds, bloodProgress) {
    this.lastFrame.time = Number.isFinite(timeSeconds) ? Math.max(0, timeSeconds) : 0;
    this.lastFrame.blood = Number.isFinite(bloodProgress) ? Math.min(1, Math.max(0, bloodProgress)) : 0;
    if (this.destroyed || !this.available || !this.gl || this.gl.isContextLost()) return false;
    var gl = this.gl;
    var width = gl.drawingBufferWidth;
    var height = gl.drawingBufferHeight;
    if (width < 1 || height < 1) return false;
    gl.viewport(0, 0, width, height);
    gl.useProgram(this.program);
    gl.uniform2f(this.uniforms.resolution, width, height);
    gl.uniform1f(this.uniforms.time, this.lastFrame.time);
    gl.uniform1f(this.uniforms.blood, this.lastFrame.blood);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    return true;
  };

  YamSuphBloodSea.prototype.handleContextLost = function (event) {
    event.preventDefault();
    this.available = false;
    this.program = null;
    this.buffer = null;
    this.uniforms = null;
  };

  YamSuphBloodSea.prototype.handleContextRestored = function () {
    if (this.destroyed) return;
    if (this.initialise()) this.render(this.lastFrame.time, this.lastFrame.blood);
  };

  YamSuphBloodSea.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this.available = false;
    if (this.canvas) {
      this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
      this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    }
    if (this.gl && !this.gl.isContextLost()) {
      if (this.buffer) this.gl.deleteBuffer(this.buffer);
      if (this.program) this.gl.deleteProgram(this.program);
    }
    this.buffer = null;
    this.program = null;
    this.uniforms = null;
    this.gl = null;
    this.canvas = null;
  };

  window.YamSuphBloodSea = YamSuphBloodSea;
}());
